import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Home, Users, MessageCircle, FileText, Sparkles, Stethoscope, Heart, Baby, Plane } from 'lucide-react';
import { mediaUrl, useSiteContent } from '../../lib/siteContent';

const ICONS = { ShieldCheck, Sparkles, Home, Users, MessageCircle, FileText, Stethoscope, Heart, Baby, Plane };

export default function WhySection() {
  const site = useSiteContent();
  const why = site.why || {};
  const reasons = Array.isArray(why.reasons) ? why.reasons : [];
  const carouselImages = (Array.isArray(why.images) ? why.images : []).filter((img) => img && (img.url || typeof img === "string"));
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (!carouselImages.length) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const slide = carouselImages[Math.min(activeSlide, Math.max(carouselImages.length - 1, 0))] || {};
  const slideUrl = mediaUrl(slide.url || slide);
  const slideCaption = slide.caption || "";

  return (
    <section id="why-us" data-testid="why-us-section" className="py-24 md:py-36 bg-[#fdfcff] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image carousel side */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <AnimatePresence mode="wait">
                <motion.img
                  key={slideUrl || activeSlide}
                  src={slideUrl}
                  alt={slideCaption}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="w-full h-auto block"
                />
              </AnimatePresence>
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#311b52]/60 via-transparent to-transparent pointer-events-none" />
              {/* Caption — right-side padding accounts for the atelier card at the corner */}
              <div className="absolute bottom-6 left-6 right-6 sm:right-[110px]">
                <p className="text-white text-sm font-medium" style={{ fontFamily: 'Montserrat' }}>
                  {slideCaption}
                </p>
                {/* Dots */}
                <div className="flex gap-2 mt-3">
                  {carouselImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveSlide(i)}
                      data-testid={`carousel-dot-${i}`}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        i === activeSlide ? 'w-8 bg-[#f37721]' : 'w-3 bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Atelier signature card — further 2.5cm down + 1.5cm left (floating signature callout) */}
            <div
              className="absolute bg-white flex flex-col items-center justify-center text-center"
              style={{
                bottom: '-96px',
                right: '-11px',
                width: '99px',
                padding: '10px 9px 9px',
                borderRadius: '14px',
                boxShadow: '0 12px 32px rgba(26, 17, 37, 0.12), 0 2px 8px rgba(26, 17, 37, 0.04)',
                gap: '6px',
              }}
              data-testid="atelier-signature-card"
            >
              {/* Tiny padlock — secure/private signal, top-left */}
              <svg
                aria-hidden="true"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#C5A059"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ position: 'absolute', top: '5px', left: '5px' }}
                data-testid="atelier-padlock"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>

              {/* BookMyJab Heart-B logo — enlarged to 34×34 for stronger brand presence */}
              <img
                src="/why/badge.png"
                alt="BookMyJab"
                width="34"
                height="34"
                style={{
                  width: '34px',
                  height: '34px',
                  objectFit: 'contain',
                  mixBlendMode: 'multiply',
                  filter: 'sepia(1) saturate(1.6) hue-rotate(-12deg) brightness(1.25) contrast(0.92)',
                  display: 'block',
                }}
                data-testid="atelier-favicon"
              />

              {/* Primary serif quote — 14px (-2pt) */}
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.15,
                  letterSpacing: '0.5px',
                  color: '#1A1125',
                }}
              >
                Care, Composed
              </div>

              {/* Thin separator */}
              <div
                style={{
                  width: '30px',
                  height: '1px',
                  backgroundColor: '#CCCCCC',
                }}
              />

              {/* Secondary couture subtitle — 8px (-2pt) */}
              <div
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 500,
                  fontSize: '8px',
                  lineHeight: 1.3,
                  textTransform: 'uppercase',
                  letterSpacing: '2.5px',
                  color: '#6B5B7B',
                }}
              >
                A Bespoke Family Experience
              </div>
            </div>
          </motion.div>

          {/* Content side */}
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: 'Montserrat' }}>{why.eyebrow}</span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mt-4 mb-10" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {why.headline}
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {reasons.map((r, i) => {
                const Icon = ICONS[r.icon] || ShieldCheck;
                return (
                <motion.div key={r.title || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="flex gap-4" data-testid={`reason-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#f37721]/8 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#f37721]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-[#311b52] mb-1" style={{ fontFamily: 'Poppins' }}>{r.title}</h4>
                    <p className="text-xs text-[#673c96]/70 leading-relaxed" style={{ fontFamily: 'Montserrat' }}>{r.desc}</p>
                  </div>
                </motion.div>
              );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
